// The seller's side of the payment stage — the Lovable design (pages/deal/SellerAfterContract.tsx)
// on the real backend. The stage asks the seller for nothing: like the mockup, they wait, then
// confirm the money arrived. Three moments:
//   waiting   — the buyer has not said anything yet;
//   reported  — the BUYER has reported paying; only now is the seller asked to confirm;
//   paid      — the seller confirmed the money arrived (the only authoritative state).
//
// While waiting, the seller may enable Swish (SellerSwishSetup): once the number that receives the
// money is confirmed the buyer is offered the QR code / Swish button, and until then they pay
// outside Klargo and report it. Enabling Swish never replaces confirming that the money arrived.
import React, { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { paymentApi, paymentErrorCode } from "./paymentApi";
import SellerSwishSetup from "./SellerSwishSetup";
import { formatPersonName } from "../../../lib/formatName";
import { formatPaymentAmount } from "./swishApp2App";
import { H1_CLASS, LEAD_CLASS, META_CLASS, Notice, firstName } from "./PaymentParts";

/**
 * How long the seller's own confirmation stands as the stage's statement before Ägarbyte takes
 * over — the reference's PAYMENT_CONFIRMED_HOLD_MS, for the same reason: the act is read before
 * the next task asks for anything. It is reading time, not motion, so it stands under reduced
 * motion too.
 */
export const PAYMENT_CONFIRMED_HOLD_MS = 1200;

// Where the stage goes next is the page's business (see BuyerPaymentPanel): the page holds the
// router. The fallback is a real navigation, so a page that forgets still works.
const defaultAdvance = (to) => { window.location.assign(to); };

// The amount in the reported lead is set in bold: it is the one figure the seller checks.
const AMOUNT_MARK = "";

export default function SellerPaymentPanel({
  state, onState, onRefresh, t, language, onAdvance = defaultAdvance, confirmedLive = false,
}) {
  const contractId = state.contractId;
  const buyerName = formatPersonName(state.buyerName) || t("payment.buyerFallback");
  const vars = { buyer: buyerName, buyerFirst: firstName(buyerName) || buyerName, amount: formatPaymentAmount(state.amount, language) };

  const stage = state.stage;
  const [pending, setPending] = useState(null);
  const [error, setError] = useState(null);
  const headingRef = useRef(null);
  const actedRef = useRef(false);

  // What the seller sees is "waiting for payment" until the buyer says otherwise. A deal with no
  // Swish number yet is the buyer's concern and does not block them, so it reads the same here.
  const view = stage === "confirmed" ? "paid"
    : stage === "reported" ? "reported"
    : "waiting";

  useEffect(() => {
    if (actedRef.current) headingRef.current?.focus({ preventScroll: true });
  }, [view]);

  // ── PAYMENT HANDOFF, the seller's half of it ──
  // The seller's own confirmation is the deal moving on with nothing left for them to decide here,
  // so the stage hands over to Ägarbyte by itself — after a hold, because the confirmation is a
  // statement worth reading before the next task appears. A payment FOUND confirmed (a reload, or
  // Back from Ägarbyte) is a different event and keeps a control, so a confirmed state alone can
  // never bounce the seller forward.
  const handoffStartedRef = useRef(false);
  useEffect(() => {
    if (!confirmedLive || handoffStartedRef.current) return undefined;
    handoffStartedRef.current = true;
    const timer = window.setTimeout(() => onAdvance(`/Ownership/${contractId}`), PAYMENT_CONFIRMED_HOLD_MS);
    return () => window.clearTimeout(timer);
  }, [confirmedLive, contractId, onAdvance]);

  const run = async (kind, call) => {
    actedRef.current = true;
    setPending(kind);
    setError(null);
    try {
      const next = await call();
      onState(next);
    } catch (err) {
      const code = paymentErrorCode(err);
      setError(t(`payment.errors.${code}`, { defaultValue: t("payment.errors.generic") }));
      if (code === "paymentStateChanged" || code === "paymentAlreadyReported") onRefresh();
    } finally {
      setPending(null);
    }
  };

  const confirmReceived = () => run("confirm", () => paymentApi.confirmReceived(contractId));

  const HEADINGS = {
    waiting: t("payment.seller.waitingTitle"),
    reported: t("payment.seller.reportedTitle", vars),
    paid: t("payment.seller.paidTitle"),
  };

  // The number the buyer says they paid: normally the current one, but it can be one the seller
  // confirmed before it changed (the buyer was already paying in Swish).
  const paidTo = state.buyerReportedSwishNumber ?? state.sellerSwishNumber;
  const paidToEarlierNumber = state.buyerReportedMethod === "swish" && !!state.buyerReportedSwishNumber
    && state.buyerReportedSwishNumber !== state.sellerSwishNumber;

  return (
    // One moment at a time, each said once: a state that arrives while the seller is on this
    // screen settles in (opacity only, as the mockup does it); the first one simply stands.
    <div key={view} className={actedRef.current ? "ds-doc-arrive" : undefined} data-testid={`seller-payment-${view}`}>
      <h1 ref={headingRef} tabIndex={-1} className={`ds-arrival-heading ${H1_CLASS} focus:outline-none`}>{HEADINGS[view]}</h1>

      {view === "waiting" && (
        <>
          <p className={LEAD_CLASS}>{t("payment.seller.waitingLead", vars)}</p>
          <SellerSwishSetup state={state} onState={onState} onRefresh={onRefresh} t={t} />
        </>
      )}

      {view === "reported" && (
        <>
          {paidToEarlierNumber && (
            <Notice tone="red" testId="seller-paid-earlier-number">
              {t("payment.seller.reportedEarlierNumber", { ...vars, number: paidTo, current: state.sellerSwishNumber })}
            </Notice>
          )}
          <p className={LEAD_CLASS}>
            {t("payment.seller.reportedLead", { ...vars, amount: AMOUNT_MARK }).split(AMOUNT_MARK).flatMap((piece, i) => (i === 0 ? [piece] : [<strong key={i} className="font-bold text-ds-ink" data-testid="seller-reported-amount">{vars.amount}</strong>, piece]))}
          </p>
          <p className={`${META_CLASS} mt-2`}>{t("payment.seller.reportedMeta")}</p>
          <div className="mt-7">
            <button type="button" onClick={confirmReceived} disabled={pending !== null}
              className="ds-action-ink w-full justify-center sm:w-auto sm:min-w-[260px] disabled:opacity-60" data-testid="seller-confirm-payment">
              {pending === "confirm" && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
              {t("payment.seller.confirm")}
            </button>
          </div>
        </>
      )}

      {view === "paid" && (
        <>
          <p className={LEAD_CLASS} data-testid="seller-payment-paid-lead">{t("payment.seller.paidLead")}</p>
          {/* Only for a payment found already confirmed: a confirmation just made moves on by
              itself once its hold is up. */}
          {!confirmedLive && (
            <div className="mt-7">
              <button type="button" onClick={() => onAdvance(`/Ownership/${contractId}`)}
                className="ds-action-ink w-full justify-center sm:w-auto sm:min-w-[260px]" data-testid="seller-payment-continue">
                {t("ownership.continue")}
              </button>
            </div>
          )}
        </>
      )}

      {error && <Notice tone="red" testId="seller-payment-error">{error}</Notice>}
    </div>
  );
}
